import React from "react";
import {
  Input,
  Select,
  SelectItem,
  Checkbox,
  Autocomplete,
  AutocompleteItem,
} from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BELGIAN_BANK_SUGGESTIONS,
  CompanyIban,
  IbanAccountType,
  IBAN_ACCOUNT_TYPE_LABELS,
} from "shared/types/yally-company";

interface CompanyIbanListProps {
  ibans: CompanyIban[];
  onChange: (ibans: CompanyIban[]) => void;
  isDisabled?: boolean;
}

const EMPTY_IBAN: CompanyIban = {
  iban: "",
  bank: "",
  type: IbanAccountType.CHECKING_ACCOUNT,
  isDefaultForRentPayments: false,
};

const CompanyIbanList: React.FC<CompanyIbanListProps> = ({
  ibans,
  onChange,
  isDisabled = false,
}) => {
  const handleAdd = () => {
    onChange([...ibans, { ...EMPTY_IBAN }]);
  };

  const handleRemove = (index: number) => {
    onChange(ibans.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    field: keyof CompanyIban,
    value: any,
  ) => {
    const updated = ibans.map((item, i) => {
      if (i !== index) {
        // When setting a new default, unset others
        if (field === "isDefaultForRentPayments" && value === true) {
          return { ...item, isDefaultForRentPayments: false };
        }
        return item;
      }
      return { ...item, [field]: value };
    });
    onChange(updated);
  };

  return (
    <div className="flex flex-col gap-3">
      {ibans.length === 0 && (
        <p className="text-sm text-gray-400">Geen IBAN-nummers toegevoegd</p>
      )}

      {ibans.map((iban, index) => (
        <div
          key={iban.id ?? `new-${index}`}
          className="flex items-start gap-3 rounded-lg border border-gray-200 p-3"
        >
          <div className="grid flex-1 grid-cols-3 gap-3">
            <Input
              label="IBAN"
              value={iban.iban}
              onChange={(e) => handleFieldChange(index, "iban", e.target.value)}
              placeholder="BE00 0000 0000 0000"
              size="sm"
              variant="bordered"
              isDisabled={isDisabled}
              classNames={{
                label: "text-sm font-medium",
                input: "text-sm",
              }}
            />
            <Autocomplete
              label="Bank"
              size="sm"
              variant="bordered"
              isDisabled={isDisabled}
              placeholder="bv. KBC"
              allowsCustomValue
              defaultItems={BELGIAN_BANK_SUGGESTIONS.map((b) => ({ key: b, label: b }))}
              selectedKey={iban.bank || null}
              inputValue={iban.bank ?? ""}
              onInputChange={(text) =>
                handleFieldChange(index, "bank", text)
              }
              onSelectionChange={(key) =>
                handleFieldChange(index, "bank", key ? String(key) : "")
              }
              classNames={{
                base: "[&_input]:text-sm",
              }}
            >
              {(item) => (
                <AutocompleteItem key={item.key}>{item.label}</AutocompleteItem>
              )}
            </Autocomplete>
            <Select
              label="Type"
              selectedKeys={iban.type ? [iban.type] : []}
              onChange={(e) =>
                handleFieldChange(index, "type", e.target.value)
              }
              size="sm"
              variant="bordered"
              isDisabled={isDisabled}
              classNames={{
                label: "text-sm font-medium",
                value: "text-sm",
              }}
            >
              {Object.values(IbanAccountType).map((type) => (
                <SelectItem key={type}>
                  {IBAN_ACCOUNT_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </Select>
            <div className="col-span-3">
              <Checkbox
                size="sm"
                isSelected={iban.isDefaultForRentPayments}
                onValueChange={(checked) =>
                  handleFieldChange(index, "isDefaultForRentPayments", checked)
                }
                isDisabled={isDisabled}
              >
                <span className="text-sm">Standaard voor huurbetalingen</span>
              </Checkbox>
            </div>
          </div>

          {!isDisabled && (
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="mt-2 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      ))}

      {!isDisabled && (
        <ButtonNew
          variant="secondaryMoss"
          onClick={handleAdd}
          icon={<Plus />}
          label="IBAN toevoegen"
        />
      )}
    </div>
  );
};

export default CompanyIbanList;
